import { t } from 'i18next';
import React, { FC, useRef } from 'react';
import styled from 'styled-components';

import { focusShadow, visuallyHidden } from '#/components/common';
import { noop, remify } from '#/service/utils';

import { Container } from '../FormInput.styled';
import { CommonInputProps } from '../FormInput.types';
import { ErrorMessage } from './ErrorMessage';
import Markdown from './Markdown';

const CHECKBOX_SIZE = '1rem';

interface InputCheckboxProps extends CommonInputProps {
  inputValue: boolean;
}

interface CheckBoxProps {
  $touched?: boolean;
}

const CheckBox = styled.input<CheckBoxProps>`
  ${visuallyHidden}

  & + label::before {
    content: '';
    position: absolute;
    top: 0;
    left: -1.3rem;
    display: inline-block;
    width: ${CHECKBOX_SIZE};
    height: ${CHECKBOX_SIZE};
    font-size: ${remify(16)};
    box-sizing: border-box;
    border: ${remify(2)} solid var(--pageParagraphForeground);
    border-radius: 0.15rem;
    text-align: center;
    line-height: ${remify(20)};
    cursor: pointer;

    ${({ theme }) => {
      const { size = 15, lineHeight = 1.6 } =
        theme.fonts?.textClasses?.FOOTNOTE || {};

      return `margin-top: calc((${size}px * ${lineHeight} - ${CHECKBOX_SIZE}) / 2);`;
    }}
  }

  &:checked + label::before {
    content: '✔';
    color: var(--selectionControlsForeground);
    background-color: var(--selectionControlsBackground);
    border: none;
  }

  &.focus-visible + label::before {
    ${focusShadow}
  }

  &:invalid + label::before {
    ${({ $touched }) =>
      $touched && `border: ${remify(2)} solid var(--inputValidation);`}
  }

  /* hover state */

  &:not(:checked) + label:hover::before {
    border: ${remify(2)} solid var(--pageParagraphForegroundSubtle);
  }

  &:checked + label:hover::before {
    background-color: var(--selectionControlsBackgroundHover);
  }

  &:invalid + label:hover::before {
    ${({ $touched }) =>
      $touched && `border: ${remify(2)} solid var(--inputValidationHover);`}
  }
`;

const StyledLabel = styled.label`
  display: block;
  margin-left: 1.3rem;
  min-height: ${remify(22)};
  position: relative;

  & * {
    font: var(--fontStyleFootnote);
  }
`;

const InputCheckbox: FC<InputCheckboxProps> = ({
  dataKey,
  errorMessages,
  inputValue,
  required,
  touched,
  label = '',
  updateErrorMessages = noop,
  updateInputValue = noop,
  handleBlur = noop,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = () => {
    const message = inputRef.current?.validationMessage;
    updateErrorMessages(dataKey, message);
  };

  const blurInput = () => {
    handleBlur();
    validate();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateInputValue(dataKey, {
      touched: true,
      value: e.target.checked,
    });
    validate();
  };

  const translatedLabel = t(label);

  return (
    <Container>
      <CheckBox
        $touched={touched}
        checked={inputValue}
        data-testid="input-checkbox"
        id={dataKey}
        name={dataKey}
        onBlur={blurInput}
        onChange={handleChange}
        ref={inputRef}
        required={required}
        type="checkbox"
        value="consentGiven"
      />
      <StyledLabel htmlFor={dataKey}>
        <Markdown linkTarget="_blank">{translatedLabel}</Markdown>
      </StyledLabel>
      {errorMessages?.map((errorMessage) => (
        <ErrorMessage key={errorMessage}>{errorMessage}</ErrorMessage>
      ))}
    </Container>
  );
};

export default InputCheckbox;
